import React, { useState } from "react";
import { useTrackEvent } from "libs/analytics";
import { LIBRARY_SEARCH_INFLUENCER } from "constants/Events";
import { InstagramUser } from "../types";
import { Button } from "@/components/ui/button";
import {
  dropdownChevronClassName,
  dropdownTriggerClassName,
} from "@/components/ui/dropdown-trigger-styles";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandEmpty,
  CommandGroup,
} from "@/components/ui/command";

export function CreatorSelector({
  requestUser,
  recentUsers,
  onCreatorSelected,
  handleRemoveCreator,
}: {
  requestUser: (val: string) => Promise<InstagramUser | undefined>;
  recentUsers: InstagramUser[];
  onCreatorSelected: (user: InstagramUser) => void;
  handleRemoveCreator: (userId: string) => void;
}) {
  const trackEvent = useTrackEvent();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [customValue, setCustomValue] = useState("");

  const openUserContent = (user: InstagramUser) => {
    onCreatorSelected && onCreatorSelected(user);
    setCustomValue("");
    setSearchValue("");
    setOpen(false);
  };

  const searchCreator = async () => {
    if (!customValue) return;

    trackEvent({
      eventName: LIBRARY_SEARCH_INFLUENCER,
      useServices: ["AMP"],
      metadata: { location: "picker" },
    });
    try {
      const searchedUser = await requestUser(customValue);
      searchedUser && openUserContent(searchedUser);
    } catch (err) {
      console.warn(err);
    }
  };

  const handleSelect = (userId: string) => {
    const user = recentUsers.find((u) => u._id === userId);
    if (user) {
      openUserContent(user);
    }
  };

  const onDelete = (event: React.MouseEvent<HTMLElement>, userId: string) => {
    event.stopPropagation();
    handleRemoveCreator && handleRemoveCreator(userId);
  };

  // Filter users based on search
  const filteredUsers = recentUsers.filter((user) =>
    user.userName.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Check if search value is a new creator (not in recent list)
  const isNewCreator =
    searchValue &&
    !recentUsers.some(
      (u) => u.userName.toLowerCase() === searchValue.toLowerCase()
    );

  React.useEffect(() => {
    if (isNewCreator) {
      setCustomValue(searchValue);
    } else {
      setCustomValue("");
    }
  }, [searchValue, isNewCreator]);

  return (
    <div className="flex items-center gap-2.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="Search for Influencer"
            className={`min-w-[280px] h-[38px] justify-between font-inter text-sm ${dropdownTriggerClassName}`}
          >
            <span
              className={
                searchValue
                  ? "text-neutral-800 truncate"
                  : "text-neutral-500 truncate"
              }
            >
              {searchValue || "Search for Influencer..."}
            </span>
            <i
              className={`fa-regular fa-chevron-down ml-2 h-4 w-4 shrink-0 ${dropdownChevronClassName}`}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" disableAnimation>
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search influencer..."
              value={searchValue}
              onValueChange={setSearchValue}
              className="border-neutral-200 font-inter text-sm"
            />
            <CommandList>
              {filteredUsers.length === 0 && !isNewCreator && (
                <CommandEmpty className="font-inter text-sm text-neutral-600">
                  No influencer added yet.
                </CommandEmpty>
              )}
              {filteredUsers.length > 0 && (
                <CommandGroup>
                  {filteredUsers.map((user) => (
                    <CommandItem
                      key={user._id}
                      value={user._id}
                      onSelect={() => handleSelect(user._id)}
                      className="font-inter"
                      data-testid="creator-option"
                    >
                      <div className="flex justify-between w-full items-center">
                        <span className="text-xs text-neutral-800 truncate max-w-[150px]">
                          {user.userName}
                        </span>
                        <button
                          onClick={(e) => onDelete(e, user._id)}
                          className="text-xs text-neutral-400 hover:text-neutral-600 cursor-pointer"
                        >
                          ×
                        </button>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Button
        variant="outline"
        disabled={!customValue}
        onClick={searchCreator}
        className="w-[94px] h-[38px] px-3 text-sm font-medium border-neutral-200 hover:border-neutral-300"
      >
        + Add
      </Button>
    </div>
  );
}
